import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { employeeApi } from '../../api/hr';
import { hrRequestApi } from '../../api/hr-requests';
import { identificationApi, qualificationApi } from '../../api/staff-master-record';
import { ApiError } from '../../api/client';
import { useToast } from '../../components/Toast';
import { compressImageToDataUri } from '../../lib/image-compress';
import { DocumentsSection } from '../../components/DocumentsSection';
import { employeeDocumentApi } from '../../api/documents';
import { CommentsThread } from '../../components/CommentsThread';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';

/**
 * Rebuilt per explicit feedback: staff can no longer edit their
 * record directly - every change goes through a real Profile Update
 * Request, reviewed by HR (same Submit/Return/Approve workflow
 * already proven for Leave/Training). Personal info is grouped into
 * real, distinct cards (Contact / Demographics / Location) instead
 * of one flat 14-field list. The old free-text "Qualifications
 * Summary" field is removed - it duplicated the real structured
 * Qualifications section below it.
 */
export function MyProfilePage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const profileQuery = useQuery({ queryKey: ['my-profile'], queryFn: () => employeeApi.getMe() });
  const documentsQuery = useQuery({ queryKey: ['my-documents'], queryFn: () => employeeDocumentApi.listMine() });
  const identificationQuery = useQuery({ queryKey: ['my-identification'], queryFn: () => identificationApi.get(profileQuery.data!.data.id), enabled: !!profileQuery.data });
  const myRequestsQuery = useQuery({ queryKey: ['my-hr-requests-profile'], queryFn: () => hrRequestApi.mine() });
  const qualificationsQuery = useQuery({ queryKey: ['my-qualifications'], queryFn: () => qualificationApi.mine() });

  const [qualForm, setQualForm] = useState({ qualificationType: 'degree', title: '', institution: '', fieldOfStudy: '', yearObtained: '' });
  const addQualMutation = useMutation({
    mutationFn: () => qualificationApi.createMine({ qualificationType: qualForm.qualificationType, title: qualForm.title, institution: qualForm.institution || undefined, fieldOfStudy: qualForm.fieldOfStudy || undefined, yearObtained: qualForm.yearObtained ? Number(qualForm.yearObtained) : undefined }),
    onSuccess: () => { showSuccess('Qualification added.'); queryClient.invalidateQueries({ queryKey: ['my-qualifications'] }); setQualForm({ qualificationType: 'degree', title: '', institution: '', fieldOfStudy: '', yearObtained: '' }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add.'),
  });
  const deleteQualMutation = useMutation({
    mutationFn: (qualId: number) => qualificationApi.deleteMine(qualId),
    onSuccess: () => { showSuccess('Removed.'); queryClient.invalidateQueries({ queryKey: ['my-qualifications'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove.'),
  });

  const [photoUploading, setPhotoUploading] = useState(false);
  const photoMutation = useMutation({
    mutationFn: (dataUri: string) => employeeApi.uploadMyPhoto(dataUri),
    onSuccess: () => { showSuccess('Photo updated.'); queryClient.invalidateQueries({ queryKey: ['my-profile'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to upload photo.'),
    onSettled: () => setPhotoUploading(false),
  });

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showError('Please select an image file.'); return; }
    setPhotoUploading(true);
    try {
      const dataUri = await compressImageToDataUri(file);
      photoMutation.mutate(dataUri);
    } catch (err) {
      setPhotoUploading(false);
      showError(err instanceof Error ? err.message : 'Failed to process the selected photo.');
    }
    e.target.value = '';
  };

  const requestUpdateMutation = useMutation({
    mutationFn: (proposedChanges: Record<string, string | null>) => hrRequestApi.createProfileUpdate(proposedChanges),
    onSuccess: () => { showSuccess('Update request submitted to HR.'); queryClient.invalidateQueries({ queryKey: ['my-hr-requests-profile'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit request.'),
  });

  if (profileQuery.isLoading) return <div className="page"><p className="muted">Loading…</p></div>;
  if (profileQuery.isError) {
    return (
      <div className="page">
        <p className="muted"><Link to="/staff/restricted-cases">Report a Concern</Link> - confidential grievance and safeguarding reporting</p>
        <p className="muted">No employee record found for your account yet. Contact HR if this seems wrong.</p>
      </div>
    );
  }

  const employee = profileQuery.data!.data;
  const contacts = profileQuery.data!.contacts;
  const pendingProfileRequests = myRequestsQuery.data?.data.filter((r) => r.request_type === 'profile_update' && ['submitted', 'under_review', 'returned'].includes(r.status)) ?? [];

  return (
    <div className="page">
      <Link to="/staff">&larr; Back to Dashboard</Link>
      <PageHeader
        title="My Profile"
        description={<><Link to="/staff/restricted-cases">Report a Concern</Link> - confidential grievance and safeguarding reporting</>}
      />

      <section className="card">
        <h2>Profile Photo</h2>
        <div className="profile-photo-row">
          {employee.profile_photo_data ? (
            <img src={employee.profile_photo_data} alt="Profile" className="profile-photo-preview" />
          ) : (
            <div className="profile-photo-placeholder">No photo yet</div>
          )}
          <label className="button-like-label">
            {photoUploading || photoMutation.isPending ? 'Uploading…' : 'Upload Photo'}
            <input type="file" accept="image/jpeg,image/png" onChange={handlePhotoChange} disabled={photoUploading || photoMutation.isPending} style={{ display: 'none' }} />
          </label>
        </div>
      </section>

      <section className="card">
        <h2>Employment Information</h2>
        <p className="muted">Managed by HR - contact HR to request a change.</p>
        <div className="project-header-grid">
          <HeaderField label="Staff ID" value={employee.staff_id ?? 'Not yet assigned'} />
          <HeaderField label="Position" value={employee.position_name ?? '-'} />
          <HeaderField label="Department" value={employee.department_name ?? '-'} />
          <HeaderField label="Duty Station" value={employee.duty_station_name ?? '-'} />
          <HeaderField label="Supervisor" value={employee.supervisor_name ?? '-'} />
          <HeaderField label="Employment Type" value={employee.employment_type ?? '-'} />
          <HeaderField label="Employment Status" value={employee.employment_status} />
          <HeaderField label="Start Date" value={employee.start_date ?? '-'} />
        </div>
      </section>

      {pendingProfileRequests.length > 0 && (
        <section className="card">
          <h2>Your Pending Update Requests</h2>
          {pendingProfileRequests.map((r) => (
            r.status === 'returned' ? (
              <ReturnedBanner key={r.id} returnComment={r.return_comment} resubmit={() => hrRequestApi.resubmit(r.id)} queryKeysToInvalidate={[['my-hr-requests-profile']]} />
            ) : (
              <p key={r.id} className="muted">Request #{r.id}: {Object.keys(r.proposed_changes ?? {}).join(', ')} - awaiting HR review.</p>
            )
          ))}
        </section>
      )}

      <RequestUpdateCard
        title="Contact Details"
        fields={[
          { key: 'phone', label: 'Phone', value: employee.phone },
          { key: 'personal_email', label: 'Personal Email', value: employee.personal_email },
          { key: 'address', label: 'Address', value: employee.address, multiline: true },
        ]}
        onSubmit={(changes) => requestUpdateMutation.mutate(changes)}
        pending={requestUpdateMutation.isPending}
      />

      <RequestUpdateCard
        title="Demographics"
        fields={[
          { key: 'preferred_name', label: 'Preferred Name', value: employee.preferred_name },
          { key: 'date_of_birth', label: 'Date of Birth', value: employee.date_of_birth, type: 'date' },
          { key: 'gender', label: 'Gender', value: employee.gender, options: [['', 'Prefer not to say'], ['male', 'Male'], ['female', 'Female'], ['other', 'Other']] },
          { key: 'marital_status', label: 'Marital Status', value: employee.marital_status, options: [['', 'Prefer not to say'], ['single', 'Single'], ['married', 'Married'], ['divorced', 'Divorced'], ['widowed', 'Widowed']] },
          { key: 'nationality', label: 'Nationality', value: employee.nationality },
        ]}
        onSubmit={(changes) => requestUpdateMutation.mutate(changes)}
        pending={requestUpdateMutation.isPending}
      />

      <RequestUpdateCard
        title="Location"
        fields={[
          { key: 'state_of_origin', label: 'State of Origin', value: employee.state_of_origin },
          { key: 'lga', label: 'LGA', value: employee.lga },
        ]}
        onSubmit={(changes) => requestUpdateMutation.mutate(changes)}
        pending={requestUpdateMutation.isPending}
      />

      <section className="card">
        <h2>Identification</h2>
        <p className="muted">Restricted, sensitive information - changes go through HR review.</p>
        {identificationQuery.data && (
          <RequestUpdateCard
            title=""
            fields={[
              { key: 'national_id_number', label: 'National ID Number', value: identificationQuery.data.data?.national_id_number ?? null },
              { key: 'passport_number', label: 'Passport Number', value: identificationQuery.data.data?.passport_number ?? null },
            ]}
            onSubmit={(changes) => requestUpdateMutation.mutate(changes)}
            pending={requestUpdateMutation.isPending}
            noCard
          />
        )}
      </section>

      <RequestUpdateCard
        title="Payroll & Statutory Information"
        description="Used by HR for payroll processing."
        fields={[
          { key: 'bank_name', label: 'Bank Name', value: employee.bank_name },
          { key: 'bank_account_number', label: 'Bank Account Number', value: employee.bank_account_number },
          { key: 'bank_account_name', label: 'Bank Account Name', value: employee.bank_account_name },
          { key: 'tax_identification_number', label: 'Tax Identification Number (TIN)', value: employee.tax_identification_number },
          { key: 'pension_pfa_name', label: 'Pension Fund Administrator (PFA) Name', value: employee.pension_pfa_name },
          { key: 'pension_pfa_number', label: 'Pension PFA Number', value: employee.pension_pfa_number },
          { key: 'nhf_number', label: 'National Housing Fund (NHF) Number', value: employee.nhf_number },
        ]}
        onSubmit={(changes) => requestUpdateMutation.mutate(changes)}
        pending={requestUpdateMutation.isPending}
      />

      <section className="card">
        <h2>Emergency Contact &amp; Next of Kin</h2>
        {contacts.length === 0 && <p className="muted">None on file yet.</p>}
        <ul className="deadline-list">
          {contacts.map((c) => (
            <li key={c.id}>
              <strong>{c.full_name}</strong> ({c.contact_type === 'next_of_kin' ? 'Next of Kin' : 'Emergency Contact'})
              {c.relationship && <span className="muted"> - {c.relationship}</span>}
              {c.phone && <span className="muted"> · {c.phone}</span>}
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h2>Qualifications</h2>
        <div className="inline-form">
          <label>
            Type
            <select value={qualForm.qualificationType} onChange={(e) => setQualForm((f) => ({ ...f, qualificationType: e.target.value }))}>
              <option value="degree">Degree</option><option value="certification">Certification</option>
              <option value="membership">Professional Membership</option><option value="language">Language</option>
            </select>
          </label>
          <label>Title<input value={qualForm.title} onChange={(e) => setQualForm((f) => ({ ...f, title: e.target.value }))} /></label>
          <label>Institution<input value={qualForm.institution} onChange={(e) => setQualForm((f) => ({ ...f, institution: e.target.value }))} /></label>
          <label>Field of Study<input value={qualForm.fieldOfStudy} onChange={(e) => setQualForm((f) => ({ ...f, fieldOfStudy: e.target.value }))} /></label>
          <label>Year<input type="number" value={qualForm.yearObtained} onChange={(e) => setQualForm((f) => ({ ...f, yearObtained: e.target.value }))} /></label>
          <button onClick={() => addQualMutation.mutate()} disabled={!qualForm.title || addQualMutation.isPending}>Add</button>
        </div>
        {qualificationsQuery.data && (
          <DataTable
            rows={qualificationsQuery.data.data}
            rowKey={(q) => q.id}
            emptyMessage="No qualifications added yet."
            columns={[
              { header: 'Title', render: (q) => q.title },
              { header: 'Institution', render: (q) => q.institution ?? '-' },
              { header: 'Year', render: (q) => q.year_obtained ?? '-' },
              { header: '', render: (q) => <button className="link-button" onClick={() => deleteQualMutation.mutate(q.id)}>Remove</button> },
            ]}
          />
        )}
      </section>

      {documentsQuery.data && (
        <DocumentsSection
          documents={documentsQuery.data.data}
          downloadUrl={employeeDocumentApi.downloadMineUrl}
          upload={employeeDocumentApi.uploadMine}
          onDelete={employeeDocumentApi.deleteMine}
          queryKeyToInvalidate={['my-documents']}
        />
      )}

      <CommentsThread entityType="employee" entityId={employee.id} />
    </div>
  );
}

interface FieldDef {
  key: string; label: string; value: string | null; type?: string; multiline?: boolean;
  options?: [string, string][];
}

/**
 * Real read-only display with a "Request Update" toggle - matches
 * the "well arranged, not a form" requirement. Editing never writes
 * directly; it only stages a proposed change and submits it as a
 * real HR request.
 */
function RequestUpdateCard({
  title, description, fields, onSubmit, pending, noCard,
}: {
  title: string; description?: string; fields: FieldDef[]; onSubmit: (changes: Record<string, string | null>) => void; pending: boolean; noCard?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const startEdit = () => {
    setDraft(Object.fromEntries(fields.map((f) => [f.key, f.value ?? ''])));
    setEditing(true);
  };

  const submit = () => {
    const changes: Record<string, string | null> = {};
    for (const f of fields) {
      const newVal = draft[f.key] ?? '';
      if (newVal !== (f.value ?? '')) changes[f.key] = newVal || null;
    }
    if (Object.keys(changes).length === 0) { setEditing(false); return; }
    onSubmit(changes);
    setEditing(false);
  };

  const content = (
    <>
      <div className="page-header">
        {title && <h2>{title}</h2>}
        {!editing && <button className="link-button" onClick={startEdit}>Request Update</button>}
      </div>
      {description && <p className="muted">{description}</p>}
      {!editing ? (
        <div className="project-header-grid">
          {fields.map((f) => <HeaderField key={f.key} label={f.label} value={f.value || '-'} />)}
        </div>
      ) : (
        <>
          {fields.map((f) => (
            <label key={f.key}>
              {f.label}
              {f.options ? (
                <select value={draft[f.key] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}>
                  {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ) : f.multiline ? (
                <textarea rows={2} value={draft[f.key] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} />
              ) : (
                <input type={f.type ?? 'text'} value={draft[f.key] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} />
              )}
            </label>
          ))}
          <div className="form-actions">
            <button onClick={submit} disabled={pending}>{pending ? 'Submitting…' : 'Submit Request to HR'}</button>
            <button className="link-button" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </>
      )}
    </>
  );

  return noCard ? content : <section className="card">{content}</section>;
}

function HeaderField({ label, value }: { label: string; value: string }) {
  return (
    <div className="project-header-field">
      <div className="project-header-field-label">{label}</div>
      <div className="project-header-field-value">{value}</div>
    </div>
  );
}
